import { useEffect, useState } from 'react';

const CLE_NAV_REDUITE = 'artosera-regie-nav-collapsed';
/** Seuil du menu mobile (mission S3-i, point 2 ; cohérent avec `halo.css`). */
const REQUETE_MOBILE = '(max-width: 849.98px)';

function lireNavReduite(): boolean {
  try {
    return window.localStorage.getItem(CLE_NAV_REDUITE) === '1';
  } catch {
    return false;
  }
}

/** Repli de la navigation latérale (216 px ⇄ 78 px), mémorisé (3.2). */
export function useNavReduite(): [boolean, (valeur: boolean) => void] {
  const [reduite, setReduite] = useState(lireNavReduite);

  const changer = (valeur: boolean): void => {
    setReduite(valeur);
    try {
      window.localStorage.setItem(CLE_NAV_REDUITE, valeur ? '1' : '0');
    } catch {
      /* stockage indisponible : le choix ne survit pas au rechargement */
    }
  };

  return [reduite, changer];
}

/** `true` sous 850 px : bascule vers le menu mobile et le scrim (3.2). */
export function useMenuMobile(): boolean {
  const [etroit, setEtroit] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(REQUETE_MOBILE).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(REQUETE_MOBILE);
    const ecouteur = (event: MediaQueryListEvent): void => setEtroit(event.matches);
    media.addEventListener('change', ecouteur);
    return () => media.removeEventListener('change', ecouteur);
  }, []);

  return etroit;
}

/**
 * `true` dès que la page a quitté le haut : la barre supérieure collante
 * (mobile) prend alors un filet d'ombre. N'écoute que le franchissement du
 * haut, pas chaque pixel de défilement.
 */
export function useDefilement(): boolean {
  const [defile, setDefile] = useState(() => typeof window !== 'undefined' && window.scrollY > 0);

  useEffect(() => {
    const lire = (): void => setDefile(window.scrollY > 0);
    lire();
    window.addEventListener('scroll', lire, { passive: true });
    return () => window.removeEventListener('scroll', lire);
  }, []);

  return defile;
}

/**
 * Le mode foire est-il actif ? (2.6, D6). `useAutoFairLayout` et
 * `FairModeToggle` posent tous deux `data-layout="fair"` sur `<html>` ; ce
 * hook l'observe pour que les écrans d'œuvres (prix en très grand, « Créer
 * une offre » seul en accent) suivent sans prop-drilling.
 */
export function useFairMode(): boolean {
  const [foire, setFoire] = useState(
    () => typeof document !== 'undefined' && document.documentElement.dataset.layout === 'fair',
  );

  useEffect(() => {
    const html = document.documentElement;
    const lire = (): void => setFoire(html.dataset.layout === 'fair');
    lire();
    const observateur = new MutationObserver(lire);
    observateur.observe(html, { attributes: true, attributeFilter: ['data-layout'] });
    return () => observateur.disconnect();
  }, []);

  return foire;
}
